/**
 * @file rep-details.tsx
 * @module app/modules/sales-management/sales-manager/sales-representative/rep-details
 * @description Implements the rep details interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import blankImg from "../../../../../_admin/assets/media/avatars/blank.png";
import Email from "../../../../../_admin/assets/media/svg_uTrade/email.svg";
import PhoneCall from "../../../../../_admin/assets/media/svg_uTrade/phone-call.svg";
import { getKey, setKey } from "../../../../../Global/history";
import Loader from "../../../../../Global/loader";
import { listSalesRepresentative } from "../../../../../utils/storeString";
import TabsAssignedCustomers from "./tabs/tabs-assigned-customers";
import TabsOrders from "./tabs/tabs-orders";
import APICallService from "../../../../../api/apiCallService";
import { assistAdmin } from "../../../../../api/apiEndPoints";
import { AllCustomers, Manager } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
const RepDetails = () => {
  const { state }: { state: any } = useLocation();
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  const [repData, setRepData] = useState<any>(null);
  const [tab, setTab] = useState([
    {
      name: "Assigned Customers",
      content: <TabsAssignedCustomers />,
      value: "1",
    },
    { name: "Orders", content: <TabsOrders />, value: "2" },
  ]);
  const [currentTab, setCurrentTab] = useState<any>(() => {
    const storedTabValue = getKey(listSalesRepresentative?.currentTab);
    const matchingTab: any = tab?.find((tab) => tab?.value == storedTabValue);
    return matchingTab || tab[1];
  });

  const createTabs = () => {
    const localTab = tab;
    let current = currentTab;
    const allTabs = localTab?.map((tab) => {
      return (
        <li className="nav-item">
          <Link
            to={"/"}
            className={
              current?.value === tab?.value
                ? "nav-link active text-active-dark"
                : "nav-link text-hover-dark"
            }
            data-bs-toggle="tab"
            onClick={() => handleSelectTab(tab)}
          >
            {tab?.name}
          </Link>
        </li>
      );
    });
    return allTabs;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchProfileData();
      setLoading(false);
    })();
  }, []);
  const fetchProfileData = async () => {
    let repId = state?.id ? state?.id : "";
    if (repId) {
      setKey(listSalesRepresentative.id, repId, false);
    } else {
      repId = getKey(listSalesRepresentative.id) || "";
    }

    let apiService = new APICallService(
      assistAdmin.info,
      "",
      { id: repId },
      "",
      "",
      "",
      AllCustomers,
      ""
    );
    let response = await apiService.callAPI();
    if (response) {
      setRepData(JSON.parse(JSON.stringify(response)));
    }
  };
  const getTabContent = (tabValue: any) => {
    const id = state?.id || getKey(listSalesRepresentative.id);
    switch (tabValue) {
      case "1":
        return <TabsAssignedCustomers representativeId={id} />;
      case "2":
        return <TabsOrders representativeId={id} salesTarget={repData?.currentSalesTarget} />;
      default:
        return null;
    }
  };
  const currentTabContent = useMemo(
    () => getTabContent(currentTab.value),
    [currentTab?.value, repData]
  );
  const handleSelectTab = (tab: any) => {
    setCurrentTab({
      ...tab,
      content: getTabContent(tab?.value),
    });
    setKey(listSalesRepresentative?.currentTab, tab.value, false);
  };
  return (
    <>
      <div className="p-5">
        <Row className="mb-7 align-items-center">
          <Col md>
            <h1 className="fs-22 fw-bolder mb-md-0 mb-3">
              {currentUser?.role.some((obj: any) => obj.type === Manager) ? "Sales " : "Activation "}Representative Details
            </h1>
          </Col>
        </Row>
        {loading ? (
          <>
            <div className="d-flex justify-content-center">
              <Loader loading={loading} />
            </div>
          </>
        ) : (
          <>
            <Card className="border border-custom-color mb-7">
              <Card.Body className="pt-9 pb-0">
                <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                  <div className="me-9">
                    <div className="symbol symbol-200px symbol-fixed border border-r10px position-relative">
                      <div className="image-input d-flex flex-center rounded w-lg-200px h-lg-200px">
                        <div
                          className="image-input-wrapper shadow-none bgi-cover bgi-position-center border-r10px w-100 h-100"
                          style={{
                            backgroundImage: `url(${repData?.image ? repData?.image : blankImg
                              })`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-center flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                      <div className="w-100 d-flex justify-content-between align-items-start flex-wrap">
                        <div className="d-flex flex-column mb-2">
                          <h3 className="text-black fs-22 fw-bolder">
                            {repData?.name} {repData?.manager?.name ? `(${repData?.manager?.name})` : ""}
                          </h3>
                          <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                            <div className="d-flex align-items-center text-black fs-16 fw-500 me-5 mb-2">
                              <span className="svg-icon svg-icon-4 me-2">
                                <img src={Email} alt="Email" />
                              </span>
                              {repData?.email}{" "}
                            </div>
                            <div className="d-flex align-items-center text-black fs-16 fw-500 mb-2">
                              <span className="svg-icon svg-icon-4 me-2">
                                <img src={PhoneCall} alt="Phone number" />
                              </span>{" "}
                              {repData?.phoneCountry} {repData?.phone}
                            </div>
                          </div>
                        </div>

                        {/* <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                          <div
                            className={`d-flex justify-content-center align-items-center py-2 px-5 pills-radius ${
                              repData?.status === "Active"
                                ? "bg-light-success"
                                : "bg-light-danger"
                            }`}
                          >
                            <span className="fw-600 text-black fs-18">
                              {repData?.status}
                            </span>
                          </div>
                        </div> */}
                      </div>
                    </div>
                    <Col lg={12}>
                      <div className="d-flex flex-wrap flex-stack">
                        <div className="d-flex flex-column flex-grow-1 pe-8">
                          <div className="d-flex flex-wrap">
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh {Method.getGeneralizedAmount(repData?.currentSalesTarget?.target ?? 0)}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">Total Target</div>
                            </div>
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(repData?.currentSalesTarget?.achieved ?? 0)}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">
                                Achieved so far
                              </div>
                            </div>
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(Math.max(
                                    0,
                                    (repData?.currentSalesTarget?.target ?? 0) -
                                    (repData?.currentSalesTarget?.achieved ?? 0)
                                  ))}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">Remaining</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </div>
                </div>
              </Card.Body>
              <Row className="align-items-center px-lg-9 px-6">
                <Col sm>
                  <div className="d-flex h-70px">
                    <ul className="nav nav-stretch nav-line-tabs nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-transparent fs-18 fw-600">
                      {createTabs()}
                    </ul>
                  </div>
                </Col>
              </Row>
            </Card>
            <div className="tab-content" id="myTabContent">
              <div className="tab-pane fade show active">
                <>{currentTabContent}</>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
export default RepDetails;
